import { useQuery } from "@tanstack/react-query";
import { fetchDetailedSubscriptions } from "@/lib/api/handlers/SubscriptionHandling";
import { SubscriptionType } from "@/types/enums/SubscriptionType";
import type { DetailedSubscription } from "@/types/Subscription";
import { expiryRung, type ExpiryRung } from "@/lib/ui/expiryTone";

/** Only the fields the banner needs — keep the projection narrow. */
const BANNER_FIELDS =
  "subscriptionId,type,companyName,cancelAtPeriodEnd,cancelAt,currentPeriodEnd,paymentPastDue";

/**
 * Exported so mutations that change billing state can invalidate it by
 * reference instead of re-typing the tuple (and drifting from it).
 */
export const EXPIRING_SUBSCRIPTION_KEY = ["dashboard", "expiringSubscription"] as const;

/** Start nudging this many days before a scheduled cancellation takes effect. */
const WARN_WITHIN_DAYS = 14;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface ExpiringSubscriptionState {
  show: boolean;
  reason: "cancel" | "pastDue" | null;
  daysRemaining: number;
  /** The machine SubscriptionType (e.g. "PROFESSIONAL") — translate via userSettings.<type>. */
  plan: SubscriptionType | null;
  subscriptionId: string | null;
  /** Escalation step — drives the banner's tone and whether it can be waved away. */
  rung: ExpiryRung;
}

const HIDDEN: ExpiringSubscriptionState = {
  show: false,
  reason: null,
  daysRemaining: 0,
  plan: null,
  subscriptionId: null,
  rung: "none",
};

function isServerErr(x: unknown): boolean {
  return !!x && typeof x === "object" && "isServerError" in x;
}

/** A company (non-event, non-free) subscription the banner cares about. */
function isCompanyPlan(sub: DetailedSubscription): boolean {
  return (
    !!sub.type &&
    sub.type !== SubscriptionType.FREE &&
    sub.type !== SubscriptionType.FREE_YEAR &&
    !sub.type.startsWith("EVENT_") &&
    sub.type !== SubscriptionType.ADDITIONAL_COLLABORATOR_COMPANY
  );
}

/**
 * Surfaces whether the current company has a subscription that is about to
 * end — either a user-scheduled cancellation approaching (`cancelAtPeriodEnd`
 * with `currentPeriodEnd`/`cancelAt` within {@link WARN_WITHIN_DAYS} days) or a
 * failed renewal payment (`paymentPastDue`). Fails closed (no banner) while
 * loading or on error. One cached query, shared across the dashboard.
 */
export function useExpiringSubscription(): ExpiringSubscriptionState {
  const q = useQuery({
    queryKey: EXPIRING_SUBSCRIPTION_KEY,
    queryFn: () => fetchDetailedSubscriptions(BANNER_FIELDS),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (!q.data || isServerErr(q.data) || !Array.isArray(q.data)) return HIDDEN;

  const now = Date.now();

  // pastDue takes priority — it's the more urgent state.
  const pastDue = q.data.find((s) => isCompanyPlan(s) && s.paymentPastDue === true);
  if (pastDue) {
    return {
      show: true,
      reason: "pastDue",
      daysRemaining: 0,
      plan: (pastDue.type as SubscriptionType) ?? null,
      subscriptionId: pastDue.subscriptionId ?? null,
      rung: expiryRung(0, true),
    };
  }

  // Scheduled cancellation approaching. Prefer currentPeriodEnd (unix secs);
  // fall back to cancelAt (ISO string) when the period end isn't populated.
  for (const sub of q.data) {
    if (!isCompanyPlan(sub) || sub.cancelAtPeriodEnd !== true) continue;
    const endMs =
      typeof sub.currentPeriodEnd === "number"
        ? sub.currentPeriodEnd * 1000
        : sub.cancelAt
          ? new Date(sub.cancelAt).getTime()
          : NaN;
    if (Number.isNaN(endMs)) continue;
    const daysRemaining = Math.ceil((endMs - now) / ONE_DAY_MS);
    if (daysRemaining < 0 || daysRemaining > WARN_WITHIN_DAYS) continue;
    return {
      show: true,
      reason: "cancel",
      daysRemaining: Math.max(0, daysRemaining),
      plan: (sub.type as SubscriptionType) ?? null,
      subscriptionId: sub.subscriptionId ?? null,
      rung: expiryRung(Math.max(0, daysRemaining)),
    };
  }

  return HIDDEN;
}
